import { useCallback, useMemo } from 'react'

import { SupportedChainId } from '~/constants/chains'
import { useTokenVisibility } from '~/hooks/org'
import {
  IAddress,
  NFTTokenBalance,
  NFTVisibilityEntry,
  TokenVisibilityEntry,
} from '~/types'

export const useGetGlobalSpamData = () => {
  const tokenMap = useTokenVisibilityMap()
  const nftMaps = useNFTVisibilityMaps()

  return useCallback(
    (params: { nft: NFTTokenBalance } | { token: ERC20Token }) => {
      const entry =
        'nft' in params
          ? getNFTVisibilityEntry(nftMaps, params.nft)
          : getTokenVisibilityEntry(tokenMap, params.token)

      return entry?.globalSpamData
    },
    [nftMaps, tokenMap],
  )
}

export const useNFTVisibilityCheck = () => {
  const nftMaps = useNFTVisibilityMaps()

  const checkVisibility = useCallback(
    (nft: NFTTokenBalance) => {
      const entry = getNFTVisibilityEntry(nftMaps, nft)

      if (entry) {
        return entry.visible
      }

      return true
    },
    [nftMaps],
  )

  return checkVisibility
}

type ERC20Token = {
  chainId: SupportedChainId
  address: IAddress
}

export const useTokenVisibilityCheck = () => {
  const tokenMap = useTokenVisibilityMap()

  const checkVisibility = useCallback(
    (token: ERC20Token) => {
      const entry = getTokenVisibilityEntry(tokenMap, token)

      if (entry) {
        return entry.visible
      }

      return true
    },
    [tokenMap],
  )

  return checkVisibility
}

// Entries the server flags as not held: tokens the org enabled before
// receipt, in either toggle state. The balance check on top covers chains
// the server does not index, where a token can arrive without the server
// noticing.
export const usePreEnabledERC20Tokens = (
  heldTokens: ERC20Token[],
): TokenVisibilityEntry[] => {
  const { tokenVisibilityEntries } = useTokenVisibility()

  return useMemo(() => {
    const heldKeys = new Set(heldTokens.map(visibilityKey))
    return tokenVisibilityEntries.filter(
      (entry) => entry.held === false && !heldKeys.has(visibilityKey(entry)),
    )
  }, [tokenVisibilityEntries, heldTokens])
}

export const usePreEnabledNFTs = (
  heldNfts: Pick<NFTTokenBalance, 'chainId' | 'address' | 'tokenId'>[],
): NFTVisibilityEntry[] => {
  const { nftVisibilityEntries } = useTokenVisibility()

  return useMemo(() => {
    const heldKeys = new Set(
      heldNfts.map((nft) => nftTokenIdKey(nft, nft.tokenId.toString())),
    )
    return nftVisibilityEntries.filter(
      (entry) =>
        entry.held === false &&
        entry.blockType === 'TokenId' &&
        !heldKeys.has(nftTokenIdKey(entry, entry.tokenId)),
    )
  }, [nftVisibilityEntries, heldNfts])
}

type TokenVisibilityMap = Map<string, TokenVisibilityEntry>

type NFTVisibilityMaps = {
  byContract: Map<string, NFTVisibilityEntry>
  byTokenId: Map<string, NFTVisibilityEntry>
}

// Entries arrive checksummed while balances may spell the same address in
// lowercase. Lowercasing makes the two match without checksumming either side.
const visibilityKey = ({
  chainId,
  address,
}: {
  chainId: number
  address: string
}) => `${chainId}:${address.toLowerCase()}`

const nftTokenIdKey = (
  nft: { chainId: number; address: string },
  tokenId: string,
) => `${visibilityKey(nft)}:${tokenId}`

// The first entry for a key wins.
const setIfAbsent = <T>(map: Map<string, T>, key: string, value: T) => {
  if (!map.has(key)) map.set(key, value)
}

const useTokenVisibilityMap = (): TokenVisibilityMap => {
  const { tokenVisibilityEntries } = useTokenVisibility()

  return useMemo(() => {
    const map: TokenVisibilityMap = new Map()
    tokenVisibilityEntries.forEach((entry) => {
      setIfAbsent(map, visibilityKey(entry), entry)
    })
    return map
  }, [tokenVisibilityEntries])
}

const useNFTVisibilityMaps = (): NFTVisibilityMaps => {
  const { nftVisibilityEntries } = useTokenVisibility()

  return useMemo(() => {
    const maps: NFTVisibilityMaps = {
      byContract: new Map(),
      byTokenId: new Map(),
    }
    nftVisibilityEntries.forEach((entry) => {
      if (entry.blockType === 'ContractAddress') {
        setIfAbsent(maps.byContract, visibilityKey(entry), entry)
      } else {
        setIfAbsent(maps.byTokenId, nftTokenIdKey(entry, entry.tokenId), entry)
      }
    })
    return maps
  }, [nftVisibilityEntries])
}

const getTokenVisibilityEntry = (map: TokenVisibilityMap, token: ERC20Token) =>
  map.get(visibilityKey(token))

// A contract-wide entry takes precedence over one for a single token id.
const getNFTVisibilityEntry = (maps: NFTVisibilityMaps, nft: NFTTokenBalance) =>
  maps.byContract.get(visibilityKey(nft)) ??
  maps.byTokenId.get(nftTokenIdKey(nft, nft.tokenId.toString()))
